import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

import { api } from '../lib/api';

export type Role = 'OWNER' | 'ADMIN' | 'TREASURER' | 'INSTRUCTOR';
export type PlanTier = 'FREE' | 'STARTER' | 'PRO' | 'ENTERPRISE';

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: Role;
  emailVerifiedAt: string | null;
  org: {
    id: string;
    slug: string;
    name: string;
    planTier: PlanTier;
    timezone: string;
    primaryColor: string | null;
    accentColor: string | null;
    logoUrl: string | null;
    onboardingComplete: boolean;
  };
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  refetch: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...allowed: Role[]) => boolean;
}

const Ctx = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<AuthUser | null> {
  try {
    const { data } = await api.get<AuthUser>('/admin/auth/me');
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { data, isPending, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 30_000,
    retry: false,
  });

  const logout = useCallback(async () => {
    try {
      await api.post('/admin/auth/logout');
    } finally {
      qc.clear();
      window.location.href = '/admin/login';
    }
  }, [qc]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: data ?? null,
      loading: isPending,
      isAuthenticated: !!data,
      refetch: async () => {
        await refetch();
      },
      logout,
      hasRole: (...allowed: Role[]) => (data ? allowed.includes(data.role) : false),
    }),
    [data, isPending, logout, refetch],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}
